import classNames from "classnames"

import { WidgetLayout } from "layouts/WidgetLayout"
import { ChartEmptyState } from "components/chart/ChartEmptyState"
import { GaugeSkeleton } from "components/chart/ChartEmptyState/skeletons"
import { RainbowChart } from "components/common/RainbowChart"
import { Spinner } from "components/common/Spinner"

import { useWidgetPhysicalStatusData } from "./useWidgetPhysicalStatusData"

import styles from "./index.module.scss"

interface Props {
  variant?: "default" | "enterprise"
}

export const WidgetPhysicalStatus = ({ variant = "default" }: Props) => {
  const { isEnterprise, isFetching, loaded, total: enterpriseTotal, widgetData } = useWidgetPhysicalStatusData(variant)
  const otherCount = widgetData.inStock + widgetData.damaged + widgetData.production + widgetData.packed

  const enterpriseStatuses = [
    {
      key: "installed",
      label: "Installed",
      value: widgetData.installed,
      colorClass: styles.installed,
    },
    {
      key: "missing",
      label: "Missing",
      value: widgetData.missing,
      colorClass: styles.missing,
    },
    {
      key: "transferring",
      label: "Transferring",
      value: widgetData.transferring,
      colorClass: styles.transfering,
    },
    {
      key: "inInventory",
      label: "In Inventory",
      value: widgetData.inInventory,
      colorClass: styles.inInventory,
    },
    {
      key: "preinstalled",
      label: "Pre-installed",
      value: widgetData.preinstalled,
      colorClass: styles.preinstalled,
    },
    {
      key: "warranty",
      label: "Warranty",
      value: widgetData.warranty,
      colorClass: styles.warranty,
    },
    {
      key: "other",
      label: "Other",
      value: otherCount,
      colorClass: styles.other,
    },
  ]
  const total = isEnterprise
    ? (enterpriseTotal ?? Object.values(widgetData).reduce((acc, current) => acc + current, 0))
    : Object.values(widgetData).reduce((acc, current) => acc + current, 0)
  const hasNoData = loaded && total === 0

  if (isEnterprise) {
    return (
      <WidgetLayout title="Thermostat Statuses" className={classNames(styles.widgetWrapper, styles.enterpriseWidget)}>
        <div className={classNames({ [styles.disabled]: isFetching })}>
          {hasNoData ? (
            <ChartEmptyState skeleton={<GaugeSkeleton />} />
          ) : (
            <>
              <div className={styles.enterpriseChart}>
                <RainbowChart
                  values={enterpriseStatuses.map(status => status.value)}
                  colors={["#86EFAC", "#FCD34D", "#2563EB", "#F3C7E9", "#14B8A6", "#58AEC3", "#94A3B8"]}
                  options={{ cutout: "58%" }}
                />
                <div className={styles.enterpriseMainValue}>
                  <span className={styles.widgetNote}>THERMOSTATS</span>
                  <div className={styles.enterpriseValue}>{total}</div>
                </div>
              </div>
              <div className={styles.enterpriseLegend}>
                {enterpriseStatuses.map(status => (
                  <div className={classNames(styles.enterpriseLegendItem, status.colorClass)} key={status.key}>
                    {status.label}
                    <span>{status.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {isFetching && (
            <div className={styles.widgetLoader}>
              <Spinner />
            </div>
          )}
        </div>
      </WidgetLayout>
    )
  }

  return (
    <WidgetLayout title="Physical status" className={styles.widgetWrapper}>
      <div
        className={classNames(styles.widgetContent, {
          [styles.disabled]: isFetching,
        })}
      >
        {hasNoData ? (
          <ChartEmptyState skeleton={<GaugeSkeleton />} />
        ) : (
          <div className={styles.chartWrapper}>
            <div className={styles.chart}>
              <RainbowChart
                values={Object.values(widgetData)}
                colors={[
                  "#14B8A6",
                  "#C11574",
                  "#22C55E",
                  "#FCD34D",
                  "#EF4444",
                  "#3B82F6",
                  "#CBD5E1",
                  "#43E0F8",
                  "#A855F7",
                  "#115E59",
                ]}
              />
              <div className={styles.widgetMainValue}>
                <span className={styles.widgetNote}>THERMOSTATS</span>
                <div className={styles.widgetValueWrapper}>
                  {Object.values(widgetData).reduce((acc, current) => acc + current, 0)}
                </div>
              </div>
            </div>
            <div className={styles.chartNotesWrapper}>
              <div className={classNames(styles.chartNote, styles.hiddenChartNote)}></div>
              <div className={classNames(styles.chartNote, styles.hiddenChartNote)}></div>
              <div className={classNames(styles.chartNote, styles.hiddenChartNote)}></div>
              <div className={classNames(styles.chartNote, styles.installed, styles.rowPreLastNote)}>
                Installed
                <span className={styles.chartNoteValue}>{widgetData.installed}</span>
              </div>
              <div className={classNames(styles.chartNote, styles.packed, styles.rowLastNote)}>
                Packed
                <span className={styles.chartNoteValue}>{widgetData.packed}</span>
              </div>
              <div className={classNames(styles.chartNote, styles.hiddenChartNote)}></div>
              <div className={classNames(styles.chartNote, styles.hiddenChartNote)}></div>
              <div className={classNames(styles.chartNote, styles.preinstalled)}>
                Pre-Installed
                <span className={styles.chartNoteValue}>{widgetData.preinstalled}</span>
              </div>
              <div className={classNames(styles.chartNote, styles.damaged, styles.rowPreLastNote)}>
                Damaged
                <span className={styles.chartNoteValue}>{widgetData.damaged}</span>
              </div>
              <div className={classNames(styles.chartNote, styles.inStock, styles.rowLastNote)}>
                In Stock
                <span className={styles.chartNoteValue}>{widgetData.inStock}</span>
              </div>
              <div className={classNames(styles.chartNote, styles.inInventory)}>
                In Inventory
                <span className={styles.chartNoteValue}>{widgetData.inInventory}</span>
              </div>
              <div className={classNames(styles.chartNote, styles.production)}>
                Production
                <span className={styles.chartNoteValue}>{widgetData.production}</span>
              </div>
              <div className={classNames(styles.chartNote, styles.missing)}>
                Missing
                <span className={styles.chartNoteValue}>{widgetData.missing}</span>
              </div>
              <div className={classNames(styles.chartNote, styles.transfering, styles.rowPreLastNote)}>
                Transferring
                <span className={styles.chartNoteValue}>{widgetData.transferring}</span>
              </div>
              <div className={classNames(styles.chartNote, styles.warranty, styles.rowLastNote)}>
                Warranty
                <span className={styles.chartNoteValue}>{widgetData.warranty}</span>
              </div>
            </div>
          </div>
        )}
        {isFetching && (
          <div className={styles.widgetLoader}>
            <Spinner />
          </div>
        )}
      </div>
    </WidgetLayout>
  )
}
